import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getClaims } from '../services/paymentsService.js';

const STATE_STYLES = {
  PAID: { bg: '#e7f4ec', color: '#1d6b40', label: 'Paid' },
  SUBMITTED: { bg: '#e8f0f7', color: '#1f4e79', label: 'Submitted' },
  CREATED: { bg: '#fdf3e3', color: '#a8520c', label: 'Created' },
  REJECTED: { bg: '#fdecea', color: '#912018', label: 'Rejected' },
  RECEIVED: { bg: '#f1ecfb', color: '#5b3fa8', label: 'Received' },
};

const getStateStyle = (state) =>
  STATE_STYLES[state] || { bg: '#eef0ee', color: '#3c4540', label: state || 'Unknown' };

const formatDateToString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ClaimsTab = ({ practiceId }) => {
  const [startDate, setStartDate] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 89);
    return formatDateToString(start);
  });
  const [endDate, setEndDate] = useState(() => formatDateToString(new Date()));

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const [hoveredDate, setHoveredDate] = useState(null);
  const datePickerRef = useRef(null);

  const [claimsData, setClaimsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [stateFilter, setStateFilter] = useState('all');
  const [carrierFilter, setCarrierFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [patientSearch, setPatientSearch] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [expandedPatients, setExpandedPatients] = useState(new Set());
  const [claimDetails, setClaimDetails] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  const loadClaims = useCallback(async () => {
    if (!practiceId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getClaims(practiceId, { startDate, endDate });
      if (data && data.success) {
        setClaimsData(data);
      } else {
        setError(data?.error || 'Failed to load claims');
        setClaimsData(null);
      }
    } catch (err) {
      console.error('Error loading claims:', err);
      setError(err.message || 'Failed to load claims');
      setClaimsData(null);
    } finally {
      setIsLoading(false);
    }
  }, [practiceId, startDate, endDate]);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  const formatDateRange = () => {
    if (!startDate || !endDate) return 'Select dates';
    const parse = (s) => {
      const [y, m, d] = s.split('-');
      return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    };
    const a = parse(startDate);
    const b = parse(endDate);
    if (startDate === endDate) return a.toLocaleDateString();
    return `${a.toLocaleDateString()} - ${b.toLocaleDateString()}`;
  };

  const openDatePicker = () => {
    setSelectingStart(true);
    setShowDatePicker(true);
  };

  const handleDateClick = (date) => {
    const dateString = formatDateToString(date);
    if (selectingStart) {
      setStartDate(dateString);
      setEndDate(dateString);
      setSelectingStart(false);
    } else {
      if (new Date(dateString) >= new Date(startDate)) {
        setEndDate(dateString);
        setShowDatePicker(false);
        setSelectingStart(true);
      } else {
        setStartDate(dateString);
        setEndDate(startDate);
        setShowDatePicker(false);
        setSelectingStart(true);
      }
    }
  };

  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setStartDate(formatDateToString(start));
    setEndDate(formatDateToString(end));
    setShowDatePicker(false);
    setSelectingStart(true);
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    const dateStr = formatDateToString(date);
    return dateStr >= startDate && dateStr <= endDate;
  };

  const isDateRangeHover = (date) => {
    if (!hoveredDate || !startDate || selectingStart) return false;
    const dateStr = formatDateToString(date);
    const startStr = startDate;
    const hoverStr = formatDateToString(hoveredDate);
    if (hoverStr >= startStr) {
      return dateStr >= startStr && dateStr <= hoverStr;
    }
    return dateStr >= hoverStr && dateStr <= startStr;
  };

  const isStartDate = (d) => startDate === formatDateToString(d);
  const isEndDate = (d) =>
    endDate === formatDateToString(d) && startDate !== endDate;

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) days.push(new Date(year, month, day));
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + direction);
      return next;
    });
  };

  const quickRangeStyle = {
    padding: '6px 10px',
    border: '1px solid #e3e7e4',
    borderRadius: '4px',
    backgroundColor: '#f7f8f7',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#2b302d',
  };

  const navButtonStyle = {
    width: '28px',
    height: '28px',
    border: '1px solid #e3e7e4',
    borderRadius: '4px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const practiceTimezone = claimsData?.timezone || 'America/New_York';

  const formatDateInTimezone = (isoString, timezone) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleDateString('en-US', {
        timeZone: timezone || 'America/New_York',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '-';
    }
  };

  const formatDateTimeInTimezone = (isoString, timezone) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleString('en-US', {
        timeZone: timezone || 'America/New_York',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '-';
    }
  };

  const allClaims = React.useMemo(() => claimsData?.claims || [], [claimsData]);
  const totals = claimsData?.totals || {
    billed: 0,
    estimated: 0,
    paid: 0,
    outstanding: 0,
    count: 0,
    paid_count: 0,
    submitted_count: 0,
  };

  const uniqueCarriers = React.useMemo(() => {
    const map = new Map();
    allClaims.forEach((c) => {
      const id = c.carrier || 'Unknown';
      if (!map.has(id)) map.set(id, { id, name: id });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allClaims]);

  const uniqueProviders = React.useMemo(() => {
    const map = new Map();
    allClaims.forEach((c) => {
      const id = c.provider_id;
      if (id && !map.has(id)) map.set(id, { id, name: c.provider_name || id });
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );
  }, [allClaims]);

  const uniqueStates = React.useMemo(() => {
    const seen = new Set();
    allClaims.forEach((c) => seen.add(c.state || 'UNKNOWN'));
    return Array.from(seen).sort();
  }, [allClaims]);

  const filteredClaims = React.useMemo(() => {
    let filtered = allClaims;
    if (stateFilter !== 'all') filtered = filtered.filter((c) => c.state === stateFilter);
    if (carrierFilter !== 'all') filtered = filtered.filter((c) => c.carrier === carrierFilter);
    if (providerFilter !== 'all')
      filtered = filtered.filter((c) => c.provider_id === providerFilter);
    if (patientSearch.trim()) {
      const q = patientSearch.trim().toLowerCase();
      filtered = filtered.filter((c) =>
        (c.patient_name || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [allClaims, stateFilter, carrierFilter, providerFilter, patientSearch]);

  const filteredTotals = React.useMemo(() => {
    return filteredClaims.reduce(
      (acc, c) => {
        acc.billed += c.total_billed_amount || 0;
        acc.estimated += c.estimated_insurance_payment || 0;
        acc.paid += c.total_paid_amount || 0;
        acc.outstanding += c.outstanding_amount || 0;
        return acc;
      },
      { billed: 0, estimated: 0, paid: 0, outstanding: 0 }
    );
  }, [filteredClaims]);

  const claimsByPatient = React.useMemo(() => {
    const grouped = {};
    filteredClaims.forEach((c) => {
      const id = c.contact_id || 'unknown';
      if (!grouped[id]) {
        grouped[id] = {
          contact_id: id,
          patient_name: c.patient_name || `Patient #${id}`,
          claims: [],
          billed: 0,
          estimated: 0,
          paid: 0,
          outstanding: 0,
          latest_claim_time: null,
        };
      }
      const g = grouped[id];
      g.claims.push(c);
      g.billed += c.total_billed_amount || 0;
      g.estimated += c.estimated_insurance_payment || 0;
      g.paid += c.total_paid_amount || 0;
      g.outstanding += c.outstanding_amount || 0;
      if (c.claim_time && (!g.latest_claim_time || c.claim_time > g.latest_claim_time)) {
        g.latest_claim_time = c.claim_time;
      }
    });
    return Object.values(grouped).sort((a, b) =>
      (b.latest_claim_time || '').localeCompare(a.latest_claim_time || '')
    );
  }, [filteredClaims]);

  const togglePatientExpanded = (id) => {
    setExpandedPatients((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderActiveFilterChips = () => {
    const chips = [];
    if (stateFilter !== 'all')
      chips.push({ label: `State: ${getStateStyle(stateFilter).label}`, clear: () => setStateFilter('all') });
    if (carrierFilter !== 'all')
      chips.push({ label: `Carrier: ${carrierFilter}`, clear: () => setCarrierFilter('all') });
    if (providerFilter !== 'all') {
      const p = uniqueProviders.find((x) => x.id === providerFilter);
      chips.push({
        label: `Provider: ${p?.name || providerFilter}`,
        clear: () => setProviderFilter('all'),
      });
    }
    if (patientSearch.trim())
      chips.push({ label: `Patient: "${patientSearch}"`, clear: () => setPatientSearch('') });
    if (chips.length === 0) return null;
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
        {chips.map((chip, i) => (
          <span
            key={i}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              background: '#e6efe9',
              color: '#1f5a3d',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {chip.label}
            <button
              onClick={chip.clear}
              style={{
                background: '#1f5a3d',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '14px',
                height: '14px',
                cursor: 'pointer',
                fontSize: '9px',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <h3 style={{ margin: 0, color: '#1f5a3d' }}>Insurance Claims</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ position: 'relative' }} ref={datePickerRef}>
              <button
                onClick={openDatePicker}
                style={{
                  padding: '8px 14px',
                  border: '1px solid #e3e7e4',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '220px',
                }}
              >
                <span>📅</span>
                <span style={{ flex: 1, textAlign: 'left' }}>{formatDateRange()}</span>
                <span style={{ fontSize: '10px', color: '#6b716d' }}>▼</span>
              </button>

              {showDatePicker && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'white',
                    border: '1px solid #e3e7e4',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    padding: '16px',
                    width: '320px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px',
                    }}
                  >
                    <h4 style={{ margin: 0, fontSize: '14px', color: '#2b302d' }}>
                      {selectingStart ? 'Select start date' : 'Select end date'}
                    </h4>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '16px',
                        cursor: 'pointer',
                        color: '#6b716d',
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '16px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <button onClick={() => setQuickRange(6)} style={quickRangeStyle}>
                      Last 7 days
                    </button>
                    <button onClick={() => setQuickRange(29)} style={quickRangeStyle}>
                      Last 30 days
                    </button>
                    <button onClick={() => setQuickRange(89)} style={quickRangeStyle}>
                      Last 90 days
                    </button>
                    <button onClick={() => setQuickRange(364)} style={quickRangeStyle}>
                      Last year
                    </button>
                  </div>

                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                      }}
                    >
                      <button onClick={() => navigateMonth(-1)} style={navButtonStyle}>
                        ‹
                      </button>
                      <span style={{ fontWeight: 500, fontSize: '14px' }}>
                        {currentMonth.toLocaleDateString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                      <button onClick={() => navigateMonth(1)} style={navButtonStyle}>
                        ›
                      </button>
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                        marginBottom: '8px',
                      }}
                    >
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                        <div
                          key={d}
                          style={{
                            textAlign: 'center',
                            fontSize: '11px',
                            color: '#8a908c',
                            fontWeight: 500,
                          }}
                        >
                          {d}
                        </div>
                      ))}
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '4px',
                      }}
                    >
                      {getDaysInMonth(currentMonth).map((date, idx) => {
                        if (!date) return <div key={`empty-${idx}`} />;
                        const isToday =
                          formatDateToString(date) === formatDateToString(new Date());
                        const isSelected = isStartDate(date) || isEndDate(date);
                        const inRange = isDateInRange(date);
                        const hover = isDateRangeHover(date);
                        return (
                          <button
                            key={date.getDate()}
                            onClick={() => handleDateClick(date)}
                            onMouseEnter={() => setHoveredDate(date)}
                            onMouseLeave={() => setHoveredDate(null)}
                            style={{
                              width: '36px',
                              height: '36px',
                              border: 'none',
                              borderRadius: isSelected ? '50%' : inRange || hover ? '0' : '4px',
                              backgroundColor: isSelected
                                ? '#1f5a3d'
                                : inRange || hover
                                ? '#e6efe9'
                                : 'transparent',
                              color: isSelected ? 'white' : isToday ? '#1f5a3d' : '#2b302d',
                              fontWeight: isToday ? 700 : 400,
                              cursor: 'pointer',
                              fontSize: '13px',
                            }}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: '1px solid #eef0ee',
                      fontSize: '12px',
                      color: '#6b716d',
                    }}
                  >
                    {formatDateRange()}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={loadClaims}
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#cdd3cf' : '#1f5a3d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
              }}
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: '#fdecea',
              color: '#912018',
              padding: '12px 16px',
              borderRadius: '4px',
              marginBottom: '20px',
            }}
          >
            {error}
          </div>
        )}

        {isLoading ? (
          <div
            style={{
              backgroundColor: '#f7f8f7',
              padding: '40px',
              borderRadius: '4px',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: 0, color: '#6b716d' }}>Loading claims data...</p>
          </div>
        ) : claimsData && !error ? (
          <>
            {/* Summary Cards */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '16px',
                marginBottom: '24px',
              }}
            >
              <div
                style={{
                  backgroundColor: '#eef0ee',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '14px', color: '#3c4540', marginBottom: '8px' }}>
                  Total Billed
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1a1d1b' }}>
                  {formatCurrency(totals.billed)}
                </div>
                <div style={{ fontSize: '12px', color: '#6b716d', marginTop: '6px' }}>
                  {totals.count} claim{totals.count !== 1 ? 's' : ''}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: '#e8f0f7',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '14px', color: '#1f4e79', marginBottom: '8px' }}>
                  Estimated Insurance
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f4e79' }}>
                  {formatCurrency(totals.estimated)}
                </div>
                <div style={{ fontSize: '12px', color: '#1f5a3d', marginTop: '6px' }}>
                  {totals.billed > 0
                    ? `${Math.round((totals.estimated / totals.billed) * 100)}% of billed`
                    : '—'}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: '#e7f4ec',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '14px', color: '#1d6b40', marginBottom: '8px' }}>
                  Paid by Insurance
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1d6b40' }}>
                  {formatCurrency(totals.paid)}
                </div>
                <div style={{ fontSize: '12px', color: '#1d6b40', marginTop: '6px' }}>
                  {totals.paid_count} settled
                </div>
              </div>

              <div
                style={{
                  backgroundColor: '#fdf3e3',
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '14px', color: '#a8520c', marginBottom: '8px' }}>
                  Outstanding
                </div>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#a8520c' }}>
                  {formatCurrency(totals.outstanding)}
                </div>
                <div style={{ fontSize: '12px', color: '#a8520c', marginTop: '6px' }}>
                  {totals.submitted_count} awaiting payment
                </div>
              </div>
            </div>

            {/* View Mode Tabs + Filters */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '8px',
                borderBottom: '2px solid #eef0ee',
                gap: '16px',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', gap: 0 }}>
                <button
                  onClick={() => setViewMode('table')}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: viewMode === 'table' ? 600 : 400,
                    color: viewMode === 'table' ? '#1f5a3d' : '#6b716d',
                    borderBottom:
                      viewMode === 'table' ? '2px solid #1f5a3d' : '2px solid transparent',
                    marginBottom: '-2px',
                  }}
                >
                  All Claims ({filteredClaims.length})
                </button>
                <button
                  onClick={() => setViewMode('byPatient')}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: viewMode === 'byPatient' ? 600 : 400,
                    color: viewMode === 'byPatient' ? '#1f5a3d' : '#6b716d',
                    borderBottom:
                      viewMode === 'byPatient' ? '2px solid #1f5a3d' : '2px solid transparent',
                    marginBottom: '-2px',
                  }}
                >
                  By Patient ({claimsByPatient.length})
                </button>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '-2px',
                  paddingBottom: '8px',
                  flexWrap: 'wrap',
                }}
              >
                {/* Patient Search */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#8a908c',
                      fontSize: '14px',
                    }}
                  >
                    🔍
                  </span>
                  <input
                    type="text"
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    placeholder="Search patient..."
                    style={{
                      padding: '6px 10px',
                      paddingLeft: '32px',
                      paddingRight: patientSearch ? '28px' : '10px',
                      border: patientSearch ? '2px solid #1f5a3d' : '1px solid #e3e7e4',
                      borderRadius: '4px',
                      fontSize: '13px',
                      backgroundColor: patientSearch ? '#e6efe9' : 'white',
                      width: '180px',
                      outline: 'none',
                    }}
                  />
                </div>

                {/* State Filter */}
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    border: stateFilter !== 'all' ? '2px solid #1f5a3d' : '1px solid #e3e7e4',
                    borderRadius: '4px',
                    fontSize: '13px',
                    backgroundColor: stateFilter !== 'all' ? '#e6efe9' : 'white',
                    cursor: 'pointer',
                    minWidth: '130px',
                    fontWeight: stateFilter !== 'all' ? 500 : 'normal',
                    color: stateFilter !== 'all' ? '#1f5a3d' : '#2b302d',
                  }}
                >
                  <option value="all">All States</option>
                  {uniqueStates.map((s) => (
                    <option key={s} value={s}>
                      {getStateStyle(s).label}
                    </option>
                  ))}
                </select>

                {/* Carrier Filter */}
                <select
                  value={carrierFilter}
                  onChange={(e) => setCarrierFilter(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    border: carrierFilter !== 'all' ? '2px solid #1f5a3d' : '1px solid #e3e7e4',
                    borderRadius: '4px',
                    fontSize: '13px',
                    backgroundColor: carrierFilter !== 'all' ? '#e6efe9' : 'white',
                    cursor: 'pointer',
                    minWidth: '170px',
                    fontWeight: carrierFilter !== 'all' ? 500 : 'normal',
                    color: carrierFilter !== 'all' ? '#1f5a3d' : '#2b302d',
                  }}
                >
                  <option value="all">All Carriers</option>
                  {uniqueCarriers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>

                {/* Provider Filter */}
                <select
                  value={providerFilter}
                  onChange={(e) => setProviderFilter(e.target.value)}
                  style={{
                    padding: '6px 10px',
                    border: providerFilter !== 'all' ? '2px solid #1f5a3d' : '1px solid #e3e7e4',
                    borderRadius: '4px',
                    fontSize: '13px',
                    backgroundColor: providerFilter !== 'all' ? '#e6efe9' : 'white',
                    cursor: 'pointer',
                    minWidth: '160px',
                    fontWeight: providerFilter !== 'all' ? 500 : 'normal',
                    color: providerFilter !== 'all' ? '#1f5a3d' : '#2b302d',
                  }}
                >
                  <option value="all">All Providers</option>
                  {uniqueProviders.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {renderActiveFilterChips()}

            {/* Filtered Totals (only show when filters active) */}
            {(stateFilter !== 'all' ||
              carrierFilter !== 'all' ||
              providerFilter !== 'all' ||
              patientSearch.trim()) && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '10px 14px',
                  background: '#f7f8f7',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#3c4540',
                  display: 'flex',
                  gap: '20px',
                  flexWrap: 'wrap',
                }}
              >
                <span>
                  Filtered totals:&nbsp;
                  <strong>{filteredClaims.length}</strong> claims
                </span>
                <span>
                  Billed: <strong>{formatCurrency(filteredTotals.billed)}</strong>
                </span>
                <span>
                  Est: <strong>{formatCurrency(filteredTotals.estimated)}</strong>
                </span>
                <span>
                  Paid: <strong>{formatCurrency(filteredTotals.paid)}</strong>
                </span>
                <span>
                  Outstanding:{' '}
                  <strong style={{ color: '#a8520c' }}>
                    {formatCurrency(filteredTotals.outstanding)}
                  </strong>
                </span>
              </div>
            )}

            <div style={{ marginTop: '16px' }}>
              {filteredClaims.length === 0 ? (
                <div
                  style={{
                    backgroundColor: '#f7f8f7',
                    padding: '20px',
                    borderRadius: '4px',
                    textAlign: 'center',
                  }}
                >
                  <p style={{ margin: 0, color: '#6b716d' }}>
                    No claims found for {formatDateRange()}
                  </p>
                </div>
              ) : viewMode === 'byPatient' ? (
                <div
                  style={{
                    border: '1px solid #e3e7e4',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  {claimsByPatient.map((patient) => {
                    const isExpanded = expandedPatients.has(patient.contact_id);
                    return (
                      <div key={patient.contact_id}>
                        <div
                          onClick={() => togglePatientExpanded(patient.contact_id)}
                          style={{
                            display: 'grid',
                            gridTemplateColumns:
                              '30px 1.4fr 110px 110px 110px 110px',
                            gap: '8px',
                            padding: '14px 15px',
                            backgroundColor: isExpanded ? '#f3f7fb' : 'white',
                            borderBottom: '1px solid #eef0ee',
                            cursor: 'pointer',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ color: '#6b716d', fontSize: '12px' }}>
                            {isExpanded ? '▼' : '▶'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: '#2b302d' }}>
                              {patient.patient_name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#8a908c' }}>
                              {patient.claims.length} claim
                              {patient.claims.length !== 1 ? 's' : ''} · last{' '}
                              {formatDateInTimezone(
                                patient.latest_claim_time,
                                practiceTimezone
                              )}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '10px', color: '#3c4540' }}>Billed</div>
                            <div style={{ fontWeight: 600, fontSize: '13px' }}>
                              {formatCurrency(patient.billed)}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '10px', color: '#1f4e79' }}>Estimated</div>
                            <div
                              style={{
                                fontWeight: 600,
                                color: '#1f4e79',
                                fontSize: '13px',
                              }}
                            >
                              {formatCurrency(patient.estimated)}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '10px', color: '#1d6b40' }}>Paid</div>
                            <div
                              style={{
                                fontWeight: 600,
                                color: '#1d6b40',
                                fontSize: '13px',
                              }}
                            >
                              {formatCurrency(patient.paid)}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '10px', color: '#a8520c' }}>Outstanding</div>
                            <div
                              style={{
                                fontWeight: 700,
                                color:
                                  patient.outstanding > 0 ? '#a8520c' : '#1d6b40',
                                fontSize: '14px',
                              }}
                            >
                              {formatCurrency(patient.outstanding)}
                            </div>
                          </div>
                        </div>

                        {isExpanded && (
                          <div
                            style={{
                              backgroundColor: '#f7f8f7',
                              borderBottom: '1px solid #e3e7e4',
                            }}
                          >
                            {patient.claims.map((c) => {
                              const stateStyle = getStateStyle(c.state);
                              return (
                                <div
                                  key={c.name || c.remote_id}
                                  style={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                      '30px 85px 85px 90px 1fr 110px 90px 90px 90px 60px',
                                    gap: '8px',
                                    padding: '10px 15px',
                                    borderBottom: '1px solid #eef0ee',
                                    fontSize: '12px',
                                    alignItems: 'center',
                                  }}
                                >
                                  <div></div>
                                  <div style={{ color: '#6b716d' }}>
                                    {formatDateInTimezone(c.claim_time, practiceTimezone)}
                                  </div>
                                  <div
                                    style={{
                                      color: c.state === 'PAID' ? '#1d6b40' : '#a3a8a4',
                                      fontWeight: c.state === 'PAID' ? 500 : 400,
                                    }}
                                    title={
                                      c.state === 'PAID'
                                        ? `Paid on ${formatDateInTimezone(c.update_time, practiceTimezone)}`
                                        : 'Not yet paid'
                                    }
                                  >
                                    {c.state === 'PAID'
                                      ? formatDateInTimezone(c.update_time, practiceTimezone)
                                      : '—'}
                                  </div>
                                  <div>
                                    <span
                                      style={{
                                        padding: '3px 6px',
                                        borderRadius: '10px',
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        backgroundColor: stateStyle.bg,
                                        color: stateStyle.color,
                                      }}
                                    >
                                      {stateStyle.label}
                                    </span>
                                  </div>
                                  <div style={{ color: '#2b302d' }}>
                                    <div style={{ fontWeight: 500 }}>{c.carrier}</div>
                                    <div style={{ color: '#8a908c', fontSize: '10px' }}>
                                      {c.group_name || c.group_number || '—'}
                                    </div>
                                  </div>
                                  <div style={{ color: '#6b716d', fontSize: '11px' }}>
                                    {c.provider_name || '—'}
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    {formatCurrency(c.total_billed_amount)}
                                  </div>
                                  <div style={{ textAlign: 'right', color: '#1d6b40' }}>
                                    {formatCurrency(c.total_paid_amount)}
                                  </div>
                                  <div
                                    style={{
                                      textAlign: 'right',
                                      color: c.outstanding_amount > 0 ? '#a8520c' : '#8a908c',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {formatCurrency(c.outstanding_amount)}
                                  </div>
                                  <div style={{ textAlign: 'center' }}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setClaimDetails(c);
                                      }}
                                      style={{
                                        backgroundColor: 'transparent',
                                        border: '1px solid #e3e7e4',
                                        borderRadius: '4px',
                                        padding: '2px 6px',
                                        cursor: 'pointer',
                                        fontSize: '10px',
                                        color: '#6b716d',
                                      }}
                                      title="View claim details"
                                    >
                                      Details
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  style={{
                    border: '1px solid #e3e7e4',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        '90px 90px 95px 1.2fr 1.2fr 110px 95px 95px 95px 64px',
                      gap: '10px',
                      padding: '12px 15px',
                      backgroundColor: '#f7f8f7',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      color: '#3c4540',
                      borderBottom: '1px solid #e3e7e4',
                    }}
                  >
                    <div>Claim Date</div>
                    <div title="When the claim was last updated (proxy for paid date when state = PAID)">
                      Paid Date
                    </div>
                    <div>State</div>
                    <div>Patient</div>
                    <div>Carrier / Plan</div>
                    <div>Provider</div>
                    <div style={{ textAlign: 'right' }}>Billed</div>
                    <div style={{ textAlign: 'right' }}>Paid</div>
                    <div style={{ textAlign: 'right' }}>Outstanding</div>
                    <div></div>
                  </div>

                  {filteredClaims.map((c, idx) => {
                    const stateStyle = getStateStyle(c.state);
                    return (
                      <div
                        key={c.name || c.remote_id || idx}
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            '90px 90px 95px 1.2fr 1.2fr 110px 95px 95px 95px 64px',
                          gap: '10px',
                          padding: '12px 15px',
                          borderBottom:
                            idx < filteredClaims.length - 1 ? '1px solid #eef0ee' : 'none',
                          backgroundColor: 'white',
                          fontSize: '13px',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{ color: '#6b716d', fontSize: '12px' }}>
                          {formatDateInTimezone(c.claim_time, practiceTimezone)}
                        </div>
                        <div
                          style={{
                            color: c.state === 'PAID' ? '#1d6b40' : '#a3a8a4',
                            fontSize: '12px',
                            fontWeight: c.state === 'PAID' ? 500 : 400,
                          }}
                          title={
                            c.state === 'PAID'
                              ? `State changed to PAID on ${formatDateInTimezone(c.update_time, practiceTimezone)}`
                              : 'Not yet paid'
                          }
                        >
                          {c.state === 'PAID'
                            ? formatDateInTimezone(c.update_time, practiceTimezone)
                            : '—'}
                        </div>
                        <div>
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: 500,
                              backgroundColor: stateStyle.bg,
                              color: stateStyle.color,
                            }}
                          >
                            {stateStyle.label}
                          </span>
                        </div>
                        <div style={{ color: '#2b302d', fontWeight: 500 }}>
                          {c.patient_name || '-'}
                          {c.payer_claim_id && (
                            <div
                              style={{
                                fontSize: '10px',
                                color: '#8a908c',
                                fontFamily: 'monospace',
                                marginTop: '2px',
                              }}
                              title="Payer Claim ID"
                            >
                              {c.payer_claim_id}
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{ color: '#2b302d', fontWeight: 500 }}>
                            {c.carrier || '—'}
                          </div>
                          <div style={{ color: '#8a908c', fontSize: '11px' }}>
                            {c.group_name || c.group_number || '—'}
                          </div>
                        </div>
                        <div style={{ color: '#6b716d', fontSize: '12px' }}>
                          {c.provider_name || '—'}
                        </div>
                        <div style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatCurrency(c.total_billed_amount)}
                        </div>
                        <div style={{ textAlign: 'right', color: '#1d6b40', fontWeight: 600 }}>
                          {formatCurrency(c.total_paid_amount)}
                        </div>
                        <div
                          style={{
                            textAlign: 'right',
                            color: c.outstanding_amount > 0 ? '#a8520c' : '#8a908c',
                            fontWeight: 700,
                          }}
                        >
                          {formatCurrency(c.outstanding_amount)}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <button
                            onClick={() => setClaimDetails(c)}
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid #e3e7e4',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              color: '#6b716d',
                            }}
                            title="View claim details"
                          >
                            Details
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : !error && (
          <div
            style={{
              backgroundColor: '#f7f8f7',
              padding: '40px',
              borderRadius: '4px',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: 0, color: '#6b716d' }}>
              Select a date range to view insurance claims
            </p>
          </div>
        )}
      </div>

      {claimDetails && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '760px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <div>
                <h3 style={{ margin: 0, color: '#2b302d' }}>Claim Details</h3>
                <div style={{ fontSize: '12px', color: '#8a908c', marginTop: '4px' }}>
                  {claimDetails.patient_name} · {claimDetails.carrier} ·{' '}
                  {getStateStyle(claimDetails.state).label}
                </div>
              </div>
              <button
                onClick={() => setClaimDetails(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b716d',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px',
                padding: '12px',
                background: '#f7f8f7',
                borderRadius: '4px',
                marginBottom: '14px',
                fontSize: '13px',
              }}
            >
              <div>
                <div style={{ color: '#8a908c', fontSize: '11px' }}>Claim Time</div>
                <div>{formatDateTimeInTimezone(claimDetails.claim_time, practiceTimezone)}</div>
              </div>
              <div>
                <div style={{ color: '#8a908c', fontSize: '11px' }}>Submitted</div>
                <div>{formatDateTimeInTimezone(claimDetails.submit_time, practiceTimezone)}</div>
              </div>
              <div>
                <div style={{ color: '#8a908c', fontSize: '11px' }}>Last Updated</div>
                <div>{formatDateTimeInTimezone(claimDetails.update_time, practiceTimezone)}</div>
              </div>
              <div>
                <div style={{ color: '#8a908c', fontSize: '11px' }}>Payer Claim ID</div>
                <div style={{ fontFamily: 'monospace' }}>
                  {claimDetails.payer_claim_id || '—'}
                </div>
              </div>
              <div>
                <div style={{ color: '#8a908c', fontSize: '11px' }}>Group / Plan</div>
                <div>
                  {claimDetails.group_name || '—'}{' '}
                  {claimDetails.group_number ? `(${claimDetails.group_number})` : ''}
                </div>
              </div>
              <div>
                <div style={{ color: '#8a908c', fontSize: '11px' }}>Carrier Phone</div>
                <div>{claimDetails.carrier_phone || '—'}</div>
              </div>
              <div>
                <div style={{ color: '#8a908c', fontSize: '11px' }}>Billed</div>
                <div>{formatCurrency(claimDetails.total_billed_amount)}</div>
              </div>
              <div>
                <div style={{ color: '#8a908c', fontSize: '11px' }}>Estimated Insurance</div>
                <div>{formatCurrency(claimDetails.estimated_insurance_payment)}</div>
              </div>
              <div>
                <div style={{ color: '#8a908c', fontSize: '11px' }}>Paid</div>
                <div style={{ color: '#1d6b40', fontWeight: 600 }}>
                  {formatCurrency(claimDetails.total_paid_amount)}
                </div>
              </div>
              <div>
                <div style={{ color: '#8a908c', fontSize: '11px' }}>Outstanding</div>
                <div
                  style={{
                    color: claimDetails.outstanding_amount > 0 ? '#a8520c' : '#8a908c',
                    fontWeight: 600,
                  }}
                >
                  {formatCurrency(claimDetails.outstanding_amount)}
                </div>
              </div>
            </div>

            {(claimDetails.procedure_associations || []).length > 0 && (
              <>
                <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: '#3c4540' }}>
                  Procedures ({claimDetails.procedure_associations.length})
                </h4>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '12px',
                    marginBottom: '14px',
                  }}
                >
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e3e7e4' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b716d' }}>
                        Procedure
                      </th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b716d' }}>
                        Billed
                      </th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6b716d' }}>
                        Est Insurance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {claimDetails.procedure_associations.map((p, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eef0ee' }}>
                        <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: '#6b716d' }}>
                          {(p.procedure || '').replace('procedures/', '') || '—'}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                          {formatCurrency(parseFloat(p.billed_amount || 0))}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: '#1f4e79' }}>
                          {p.estimated_insurance_payment
                            ? formatCurrency(parseFloat(p.estimated_insurance_payment))
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <div style={{ marginTop: '14px', textAlign: 'right' }}>
              <button
                onClick={() => setClaimDetails(null)}
                style={{
                  backgroundColor: '#1f5a3d',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsTab;
